import { CSSProperties, useState } from 'react'
import Slider, { SliderProps } from 'rc-slider'
import 'rc-slider/assets/index.css'
import { HandleTooltip } from './SliderToolTip'
import { classNames } from 'src/lib/utils/helpers'

const handleHeight = 10
const handleWidth = 28
const defaultHandleStyle: CSSProperties = {
  height: 50,
  width: handleWidth,
  cursor: 'grab',
  top: '50%',
  borderRadius: 8,
  opacity: 1,
  border: '1px solid var(--flo-gray-dark-color)',
  transform: `translate(calc(-${handleWidth / 2}px), calc(-50% + ${handleHeight / 2}px))`
}

interface RTORangeSliderProps {
  initialValues?: number[]
  minDistance?: number
  handleChange?: (values: number[]) => void
  suggestedRanges?: { min: number; max: number }
}

// Specifically built for RTO risk threshold
// There is no design pattern present for sliders at this moment
// which means it's not built for reusability, just for completing specifc usecase
export const RTORangeSlider = ({
  initialValues = [0, 35, 55, 100],
  minDistance = 1,
  handleChange,
  suggestedRanges,
  ...restProps
}: RTORangeSliderProps) => {
  const [values, setValues] = useState(initialValues)

  // Custom Mark Component
  const CustomMark = ({ label, disableMark, height }: { label: string; disableMark?: boolean; height?: number }) => (
    <div className='flex flex-col items-center -translate-y-1/2'>
      <div
        onClick={e => e.stopPropagation()}
        className={classNames(
          'mb-2 cursor-default',
          height ? 'h-[40px]' : 'h-[90px]',
          disableMark ? '' : 'border-l border-dashed border-gray-dark'
        )}
      />
      <span className='text-coal-light text-xs'>{label}</span>
    </div>
  )

  const marks = {
    15: <CustomMark height={50} disableMark label='LOW RISK' />,
    50: <CustomMark height={50} disableMark label='MEDIUM RISK' />,
    85: <CustomMark height={50} disableMark label='HIGH RISK' />,
    ...(suggestedRanges?.min ? { [suggestedRanges.min]: <CustomMark label={suggestedRanges.min.toString()} /> } : {}),
    ...(suggestedRanges?.max ? { [suggestedRanges.max]: <CustomMark label={suggestedRanges.max.toString()} /> } : {}),
    99: <CustomMark label='100' disableMark />
  }

  const onChange = (newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      let sliderValues = [0, newValue[1], newValue[2], 100]
      const minDistanceReached = newValue[2] - newValue[1] <= minDistance - 1
      if (minDistanceReached) {
        setValues(prev => {
          sliderValues = [0, prev[1], prev[2], 100]
          return sliderValues
        })
        return
      }
      setValues(sliderValues)
      handleChange?.(sliderValues)
    }
  }

  const handleRender: SliderProps['handleRender'] = (node, props) => {
    const tipFormat: 'HIGH' | 'LOW' = props.index === 1 ? 'LOW' : 'HIGH'
    const tipFormatter = (value: number) => (
      <div className='bg-white px-4 py-2 rounded-xl whitespace-nowrap border border-gray-light shadow-sm'>
        <div className='mb-1 flex space-x-1 items-center justify-center'>
          <span className='text-coal-dark text-sm'>{tipFormat === 'LOW' ? 'Low risk' : 'High risk'}</span>
          <span
            className={classNames('text-sm font-semibold', tipFormat === 'LOW' ? 'text-green-dark' : 'text-red-dark')}
          >
            {value}
          </span>
        </div>
        <div className='text-sm text-gray-dark'>
          Recommended {tipFormat === 'LOW' ? suggestedRanges?.min : suggestedRanges?.max}
        </div>
      </div>
    )

    return (
      <HandleTooltip value={props.value} visible={props.dragging} tipFormatter={tipFormatter}>
        {node}
      </HandleTooltip>
    )
  }

  // Some properties are deprecated, a this moment there is no alternative props mentioned in docs
  // Only way to is to change properties by modifying css of library
  return (
    <div className='w-full flex justify-center items-center min-h-[100px]'>
      <Slider
        marks={marks}
        handleStyle={[{ display: 'none' }, defaultHandleStyle, defaultHandleStyle, { display: 'none' }]}
        railStyle={{ height: 10 }}
        trackStyle={[
          { backgroundColor: 'var(--flo-yay-color)', height: 10, zIndex: 1 },
          { backgroundColor: 'var(--flo-yellow-light-color)', height: 10, zIndex: 1 },
          { backgroundColor: 'var(--flo-red-dark-color)', height: 10, zIndex: 1 }
        ]}
        value={values}
        step={1}
        range={{ maxCount: 4 }}
        allowCross={false}
        dots={false}
        onChange={onChange}
        handleRender={handleRender}
        activeDotStyle={{ display: 'none' }}
        {...restProps}
      />
    </div>
  )
}
